<template>
  <el-dialog
    ref="dialog"
    transition="rt-fade-ease"
    :class="classes"
    :visible.sync="isVisible"
    v-bind="$attrs"
    :width="computedFullScreenSize || width"
    :height="computedFullScreenSize || computedHeight"
    :bg-color="bgColor"
    :header-bg-color="headerBgColor"
    :body-bg-color="bodyBgColor"
    :footer-bg-color="footerBgColor"
    :append-to-body="appendToBody"
    @close="close"
    @fullscreenchange.native="handleIsBrowserFullScreen"
  >
    <div
      slot="title"
      class="rt-dialog-title-wrapper"
    >
      <slot name="title">
        <div class="rt-dialog-title-left">
          {{ title }}
        </div>
        <div class="rt-dialog-title-right">
          <!-- 抽屉/分栏icon -->
          <rt-tooltip
            v-if="slideLayout"
            :content="computedToolTip"
            placement="bottom"
          >
            <rt-icon
              type="iconFont"
              :name="computedIconName"
              @click="handleClickIcon"
            />
          </rt-tooltip>
          <!-- 网页全屏 -->
          <rt-tooltip
            v-if="fullScreenPage"
            :content="isPageFullScreen ? $tx('退出网页全屏', 'xpc.dialog.exitBrowserFullScreen') : $tx('网页全屏', 'xpc.dialog.browserFullScreen')"
            placement="bottom"
          >
            <rt-icon
              type="iconFont"
              :name="
                isPageFullScreen
                  ? 'rt-base-exit-page-full-screen-lined'
                  : 'rt-base-page-full-screen-lined'
              "
              @click="togglePageFullScreen"
            />
          </rt-tooltip>
          <!-- 浏览器全屏 -->
          <rt-tooltip
            v-if="fullScreenBrowser"
            :content="isBrowserFullScreen ?$tx('退出全屏', 'xpc.dialog.exitFullScreen') : $tx('全屏', 'xpc.dialog.fullScreen')"
            placement="bottom"
          >
            <rt-icon
              type="iconFont"
              :name="
                isBrowserFullScreen
                  ? 'rt-base-exit-full-screen-lined'
                  : 'rt-base-full-lined'
              "
              @click="toggleBrowserFullScreen"
            />
          </rt-tooltip>
          <!-- 关闭按钮 -->
          <rt-icon
            v-if="showClose"
            name="rt-base-close-x-lined"
            @click="hide"
          />
        </div>
      </slot>
      <slot name="title-button"></slot>
    </div>
    <div
      class="rt-dialog-content-wrapper"
      :class="!isShowColumn || !slideLayout ? 'rt-dialog-content-wrapper-visible' : ''"
    >
      <slot></slot>
    </div>
    <!-- 抽屉 -->
    <rt-drawer
      v-if="slideLayout === 'drawer'"
      :append-to-body="false"
      :with-header="false"
      :size="drawerSize"
      :modal="false"
      :direction="computedDirection"
      :visible="isShowDrawer"
    >
      <slot name="drawer"></slot>
    </rt-drawer>
    <!-- 分栏 -->
    <div
      v-if="slideLayout === 'column'"
      :class="[
        'rt-dialog-column',
        { 'rt-dialog-column-visible': isShowColumn }
      ]"
      :style="{
        width: computedColumnWidth,
        height: computedColumnHeight
      }"
    >
      <slot name="column"></slot>
    </div>
    <div
      v-if="showFooter"
      slot="footer"
      class="rt-dialog-footer-wrapper"
      :class="$slots.footer ? 'rt-dialog-footer' : ''"
    >
      <slot name="footer">
        <div class="rt-dialog-footer-left">
          <slot name="footer-left"></slot>
        </div>
        <div class="rt-dialog-footer-buttons">
          <slot name="footer-right">
            <rt-button @click="cancel">
              {{ $tx('取消', 'xpc.dialog.cancel') }}
            </rt-button>
            <rt-button
              type="primary"
              @click="confirm"
            >
              {{ $tx('确认', 'xpc.dialog.confirm') }}
            </rt-button>
          </slot>
        </div>
      </slot>
    </div>
  </el-dialog>
</template>
<script>
import xpcConfig from 'vue-xpc/src/config';
import rtButton from '../button/rtButton';
import elDialog from 'vue-xpc/src/element-ui/packages/dialog';
export default {
  name: 'RtDialog',
  components: {
    rtButton,
    elDialog
  },
  props: {
    title: String,
    value: {
      type: Boolean,
      default: false
    },
    validate: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'normal',
      validator (value) {
        return ['large', 'normal', 'small', ''].includes(value);
      }
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    showClose: {
      type: Boolean,
      default: true
    },
    /**
     * @public 自定义dialog宽度
     */
    width: {
      type: String,
      default: ''
    },
    /**
     * @public 自定义dialog高度
     */
    height: {
      type: String,
      default: ''
    },
    /**
     * @public 允许全屏和浏览器全屏
     */
    fullScreenPage: {
      type: Boolean,
      default: false
    },
    fullScreenBrowser: {
      type: Boolean,
      default: false
    },
    /**
     * @public 隐藏分隔线
     */
    hideDivider: {
      type: [Boolean, String],
      default: false,
      validator (value) {
        return [true, false, 'header', 'footer', 'column'].includes(value);
      }
    },
    /**
     * @public dialog背景色
     */
    bgColor: {
      type: String,
      default: null,
      validator (value) {
        return ['n-lighter'].includes(value);
      }
    },
    /**
     * @public header背景色
     */
    headerBgColor: {
      type: String,
      default: null,
      validator (value) {
        return ['n-lighter'].includes(value);
      }
    },
    /**
     * @public body背景色
     */
    bodyBgColor: {
      type: String,
      default: null,
      validator (value) {
        return ['n-lighter'].includes(value);
      }
    },
    /**
     * @public footer背景色
     */
    footerBgColor: {
      type: String,
      default: null,
      validator (value) {
        return ['n-lighter'].includes(value);
      }
    },
    /** * @public 滑动布局是抽屉/分栏 */
    slideLayout: {
      type: String,
      default: ''
    },
    /** * @public 配置抽屉/分栏方向 'top' 'right' 'bottom' 'left' */
    slideDirection: {
      type: String,
      default: 'right'
    },
    /** * @public 抽屉模式下是否默认展开 */
    showDrawer: {
      type: Boolean,
      default: false
    },
    /** * @public 分栏模式下是否默认展开 */
    showColumn: {
      type: Boolean,
      default: false
    },
    drawerSize: {
      type: [Number, String],
      default: '30%'
    },
    columnWidthRatio: {
      type: String,
      default: '2:1'
    },
    columnHeightRatio: {
      type: String,
      default: '2:1'
    },
    appendToBody: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isVisible: this.value,
      isBrowserFullScreen: false,
      isPageFullScreen: false,
      isShowDrawer: this.showDrawer,
      isShowColumn: this.showColumn
    };
  },
  computed: {
    classes () {
      return [
        'rt-dialog-wrapper',
        { 'rt-dialog-wrapper-chrome-below-75': this.$xpc.isChromeBelow75() },
        { 'rt-dialog-hide-divider': this.hideDivider === true },
        {
          [`rt-dialog-hide-${this.hideDivider}-divider`]:
            typeof this.hideDivider === 'string'
        },
        {
          [`rt-dialog-${this.size}`]: !(
            this.isBrowserFullScreen || this.isPageFullScreen
          )
        },
        { 'rt-dialog-drawer': this.slideLayout === 'drawer' },
        {
          [`rt-dialog-column-${this.slideDirection}`]:
            this.slideLayout && this.slideDirection
        }
      ];
    },
    computedHeight () {
      return this.height || xpcConfig.get('dialog.size.height')[this.size];
    },
    computedFullScreenSize () {
      return this.isBrowserFullScreen || this.isPageFullScreen ? '100%' : '';
    },
    computedColumnWidth () {
      if (this.slideDirection === 'top' || this.slideDirection === 'bottom') {
        return '100%';
      } else {
        if (this.isShowColumn) {
          return this.getColumnRatio(this.columnWidthRatio);
        } else {
          return 0;
        }
      }
    },
    computedColumnHeight () {
      if (this.slideDirection === 'right' || this.slideDirection === 'left') {
        return '100%';
      } else {
        if (this.isShowColumn) {
          return this.getColumnRatio(this.columnHeightRatio);
        } else {
          return 0;
        }
      }
    },
    // 抽屉方向
    computedDirection () {
      let direction = 'rtl';
      switch (this.slideDirection) {
        case 'top':
          direction = 'ttb';
          break;
        case 'right':
          direction = 'rtl';
          break;
        case 'bottom':
          direction = 'btt';
          break;
        case 'left':
          direction = 'ltr';
          break;
      }
      return direction;
    },
    // 图标tooltip悬浮文字和icon名字
    computedToolTip () {
      let content;
      switch (this.slideDirection) {
        case 'top':
          content =
            this.isShowDrawer || this.isShowColumn
              ? this.$tx('收起', 'xpc.dialog.collapse')
              : this.$tx('向下展开', 'xpc.dialog.expandDown');
          break;
        case 'right':
          content =
            this.isShowDrawer || this.isShowColumn
              ? this.$tx('收起', 'xpc.dialog.collapse')
              : this.$tx('向左展开', 'xpc.dialog.expandLeft');
          break;
        case 'bottom':
          content =
            this.isShowDrawer || this.isShowColumn
              ? this.$tx('收起', 'xpc.dialog.collapse')
              : this.$tx('向上展开', 'xpc.dialog.expandUp');
          break;
        case 'left':
          content =
            this.isShowDrawer || this.isShowColumn
              ? this.$tx('收起', 'xpc.dialog.collapse')
              : this.$tx('向右展开', 'xpc.dialog.expandRight');
          break;
      }
      return content;
    },
    computedIconName () {
      let icon;
      switch (this.slideDirection) {
        case 'top':
          icon =
            this.isShowDrawer || this.isShowColumn
              ? 'rt-base-retract-expand-down-lined'
              : 'rt-base-expand-down-lined';
          break;
        case 'right':
          icon =
            this.isShowDrawer || this.isShowColumn
              ? 'rt-base-retract-expand-left-lined'
              : 'rt-base-expand-left-lined';
          break;
        case 'bottom':
          icon =
            this.isShowDrawer || this.isShowColumn
              ? 'rt-base-retract-expand-up-lined'
              : 'rt-base-expand-up-lined';
          break;
        case 'left':
          icon =
            this.isShowDrawer || this.isShowColumn
              ? 'rt-base-retract-expand-right-lined'
              : 'rt-base-expand-right-lined';
          break;
      }
      return icon;
    }
  },
  watch: {
    value: {
      handler (newVal) {
        this.isVisible = newVal;
        this.isShowDrawer = this.showDrawer || false;
        this.isShowColumn = this.showColumn || false;
      },
      deep: true
    },
    isBrowserFullScreen (newVal) {
      if (newVal) this.isPageFullScreen = false;
    },
    isPageFullScreen (newVal) {
      if (newVal) this.isBrowserFullScreen = false;
    }
  },
  methods: {
    getColumnRatio (ratio) {
      const columnRatio = ratio.split(':');
      return (
        (parseInt(columnRatio[1]) /
          (parseInt(columnRatio[0]) + parseInt(columnRatio[1]))) *
          100 +
        '%'
      );
    },
    hide () {
      this.isVisible = false;
    },
    close () {
      this.isVisible = false;
      this.isBrowserFullScreen = false;
      this.isPageFullScreen = false;
      this.$emit('input', this.isVisible);

      this.$setTimeout(() => {
        this.$emit('close');
      }, 300);
    },
    confirm () {
      this.$emit('confirm');
      if (this.validate) return;
      this.hide();
    },
    cancel () {
      this.$emit('cancel');
      this.hide();
    },
    toggleBrowserFullScreen () {
      let elem = this.$refs.dialog.$el;
      if (this.isBrowserFullScreen) {
        this.isBrowserFullScreen = false;
        document.exitFullscreen();
      } else {
        this.isBrowserFullScreen = true;
        elem.requestFullscreen && elem.requestFullscreen();
      }
    },
    togglePageFullScreen () {
      if (this.isBrowserFullScreen) document.exitFullscreen();
      this.isPageFullScreen = !this.isPageFullScreen;
    },
    handleIsBrowserFullScreen () {
      if (!document.fullscreenElement) this.isBrowserFullScreen = false;
    },
    handleClickIcon () {
      const layout =
        this.slideLayout === 'drawer' ? 'isShowDrawer' : 'isShowColumn';
      this[layout] = !this[layout];
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtDialog.vue?7ebeafef